/**
 * The live view of a Boat browser. Camoufox has no CDP: the live view on the
 * browser's own machine (`LIVE_VIEW_SCRIPT`, buy-on-behalf's
 * boat-scripts.ts) shows it instead, over one WebSocket at the gated
 * address the attempt names. It sends images of the part of the page the
 * buyer needs, the bank's frame alone where there is one, each with where
 * it sits in the page; it takes which check the buyer is shown, taps,
 * typing, Backspace and Enter, and scrolls, and nothing else: unlike a CDP host, the address is not control
 * of the browser. No React and no DOM beyond `WebSocket`.
 */
import { isBoatLiveView } from "./cdp";
import type { LiveTransport, Point, TransportOptions } from "./transport";

/** How long a tap waits to hear whether it focused something to type into. */
const TAP_ANSWER_MS = 5000;
/** The live view takes typing in pieces this long at most. */
const TYPED_PIECE = 64;
/** How far, in the page's pixels, a finger moves before it scrolls: a tap
 *  that wobbles scrolls nothing from under itself. */
const SCROLL_SLOP = 8;

type Message =
  | { t: "frame"; x: number; y: number; width: number; height: number; type: "jpeg" | "png"; data: string }
  | { t: "tapped"; id: number; editable: boolean };

export async function openBoatTransport(url: string, options: TransportOptions): Promise<LiveTransport> {
  if (!isBoatLiveView(url)) throw new Error("Not a live view host");
  const ws = new WebSocket(url);
  let closed = false;
  let open = false;
  // Where the shown part sits in the page: a point in it is offset by this.
  let origin = { x: 0, y: 0 };
  let next = 1;
  const taps = new Map<number, (editable: boolean) => void>();
  const send = (message: object) => {
    if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(message));
  };
  const inPage = (point: Point) => ({ x: Math.round(origin.x + point.x), y: Math.round(origin.y + point.y) });
  ws.onmessage = (event) => {
    let message: Message;
    try { message = JSON.parse(String(event.data)) as Message; } catch { return; }
    if (message.t === "frame") {
      origin = { x: message.x, y: message.y };
      options.onFrame({ data: message.data, width: message.width, height: message.height, type: message.type });
    } else if (message.t === "tapped") {
      taps.get(message.id)?.(message.editable === true);
      taps.delete(message.id);
    }
  };
  ws.onclose = () => {
    for (const answer of taps.values()) answer(false);
    taps.clear();
    if (open && !closed) options.onClose();
  };
  await new Promise<void>((resolve, reject) => {
    ws.onopen = () => resolve();
    ws.onerror = () => reject(new Error("Could not open the browser's connection"));
  });
  open = true;
  // What it crops to: the bank's page, or the robot check's widget.
  if (options.check) send({ t: "check", kind: options.check });

  // A drag scrolls what is under where it started, by as far as it moved.
  // `at` is that spot in the page, kept from the finger's going down: a
  // tap there is clicked at it though a newer crop has moved the view since.
  let drag: { point: Point; at: Point; from: number; last: number; scrolling: boolean } | null = null;
  return {
    drag(phase, point) {
      if (phase === "start" && point) drag = { point, at: inPage(point), from: point.y, last: point.y, scrolling: false };
      else if (phase === "move" && point && drag) {
        drag.scrolling ||= Math.abs(point.y - drag.from) > SCROLL_SLOP;
        if (!drag.scrolling) return;
        const dy = Math.round(drag.last - point.y);
        drag.last = point.y;
        if (dy) send({ t: "scroll", ...drag.at, dy });
      } else if (phase === "end") drag = null;
    },
    tap(point, focus) {
      const at = drag && drag.point.x === point.x && drag.point.y === point.y ? drag.at : inPage(point);
      drag = null;
      const id = next++;
      return new Promise<boolean>((resolve) => {
        const timer = setTimeout(() => { taps.delete(id); resolve(false); }, TAP_ANSWER_MS);
        taps.set(id, (editable) => { clearTimeout(timer); resolve(focus && editable); });
        send({ t: "tap", id, ...at });
      });
    },
    type(text) {
      for (let at = 0; at < text.length; at += TYPED_PIECE) send({ t: "type", text: text.slice(at, at + TYPED_PIECE) });
    },
    key(key) {
      send({ t: "key", key });
    },
    show(check) {
      send({ t: "check", kind: check });
    },
    close() {
      closed = true;
      ws.close();
    },
  };
}
